import { useMemo } from 'react';

import { Listbox } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { usePreferences } from '@/stores/preferences-store';

/**
 * Currency selection, restricted to what the church actually uses.
 *
 * Defaults to the church's supported list rather than every ISO 4217 code, because a
 * church in Accra choosing between two hundred currencies is a church about to record
 * an offering in the wrong one.
 */

export interface CurrencySelectProps {
  value: string | null;
  onChange: (currency: string | null) => void;
  /** The church's supported currencies. Falls back to its base currency alone. */
  currencies?: string[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
  'aria-describedby'?: string;
}

export const CurrencySelect = ({
  value,
  onChange,
  currencies,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled,
  invalid,
  id,
  popoverPortal = 'body',
  className,
  ...aria
}: CurrencySelectProps) => {
  const { locale, baseCurrency } = usePreferences();
  const available = currencies?.length ? currencies : [baseCurrency];
  // A new array identity on every render would rebuild the list each time; the codes
  // themselves are what actually change.
  const availableKey = available.join(',');

  const options = useMemo(
    () =>
      availableKey.split(',').map((code) => ({
        value: code,
        label: code,
        description: currencyName(code, locale),
        keywords: [currencyName(code, locale)],
      })),
    [availableKey, locale],
  );

  return (
    <Listbox
      value={value}
      onChange={onChange}
      options={options}
      searchable={options.length > 8}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      disabled={disabled}
      invalid={invalid}
      id={id}
      popoverPortal={popoverPortal}
      className={className}
      {...aria}
    />
  );
};

const currencyName = (code: string, locale: string): string => {
  try {
    return new Intl.DisplayNames([locale], { type: 'currency' }).of(code) ?? code;
  } catch {
    return code;
  }
};
